import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import kebabCase from 'lodash/kebabCase';

import { PageHeader } from 'inside-out/components/shared/pageHeader';
import { Cards } from 'inside-out/components/shared/cards';
import { SupportCard } from 'inside-out/components/shared/cards/supportCard';
import AudienceIcon from 'inside-out/components/shared/icons/audienceIcon';
import BG from 'inside-out/images/support-organisations/bg.svg';
import { FilterDropDown } from 'inside-out/components/shared/filterDropDown';
import { AudienceFilter } from 'inside-out/components/shared/audienceFilter';
import { navigateTo } from 'inside-out/components/shared/link';
import { Pagination } from 'inside-out/components/shared/pagination';

import { idToLink } from 'inside-out/managers/navManager';
import {
	fullAudiences,
	linkWithFilter,
	prepareRegions,
} from 'inside-out/managers/supportOrganisationsManager';

import {
	tabPageWrapper,
	locationSelectorStyle,
	locationSelectorInnerStyle,
	locationDropdownStyle,
} from 'inside-out/page-styles/support-organisations';

import { sideBarLayout } from 'inside-out/global-styles';

const regionPropType = PropTypes.shape({
	edges: PropTypes.arrayOf(
		PropTypes.shape({
			node: PropTypes.shape({
				title: PropTypes.string,
				parent_region: PropTypes.string,
			}),
		})
	),
});

export default class SupportOrganisationsBrowsePage extends React.PureComponent {
	static propTypes = {
		pathContext: PropTypes.shape({
			pageNumber: PropTypes.number.isRequired,
			totalPages: PropTypes.number.isRequired,
			parentRegion: PropTypes.string,
			childRegion: PropTypes.string,
			audience: PropTypes.string,
		}).isRequired,
		data: PropTypes.shape({
			parentRegions: regionPropType,
			childRegions: regionPropType,
			page: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							heading: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
						}),
					})
				),
			}),
			organisations: PropTypes.shape({
				edges: PropTypes.arrayOf(
					PropTypes.shape({
						node: PropTypes.shape({
							type: PropTypes.string.isRequired,
							title: PropTypes.string.isRequired,
							extract: PropTypes.string.isRequired,
							body: PropTypes.string.isRequired,
							region: PropTypes.string.isRequired,
							audiences: PropTypes.arrayOf(PropTypes.string),
							attachments: PropTypes.arrayOf(
								PropTypes.shape({
									file: PropTypes.string,
								})
							),
							contact_information: PropTypes.arrayOf(
								PropTypes.shape({
									type: PropTypes.string,
									text: PropTypes.string,
									destination: PropTypes.string,
								})
							),
							fields: PropTypes.shape({
								thumbnail_image: PropTypes.shape({
									childImageSharp: PropTypes.shape({
										resize: PropTypes.shape({
											src: PropTypes.string,
										}),
									}),
								}),
							}),
						}),
					})
				),
			}).isRequired,
		}).isRequired,
	};

	constructor(props, context) {
		super(props, context);

		this.regions = prepareRegions(props.data);
	}

	audienceFilter = () => (
		<AudienceFilter
			audiences={[
				{ id: 'for-healthcare-professionals', title: 'For Health Professionals' },
				{ id: 'for-myself', title: 'For Myself' },
				{ id: 'family-and-carers', title: 'For Family & Carers' },
			].map(a => ({
				...a,
				icon: <AudienceIcon audience={a} size={12} />,
			}))}
			activeFilters={[]}
			onChange={activeFilters => {
				const link = linkWithFilter({ audiences: activeFilters });
				navigateTo(link);
			}}
		/>
	);

	selectParentRegion(e) {
		const link = linkWithFilter({ parentRegion: e.target.value });
		navigateTo(link);
	}

	selectChildRegion(e) {
		const link = linkWithFilter({
			parentRegion: kebabCase(this.props.pathContext.parentRegion),
			childRegion: e.target.value,
		});
		navigateTo(link);
	}

	onPageChange = page => {
		const link = linkWithFilter({
			parentRegion: kebabCase(this.props.pathContext.parentRegion),
			childRegion: kebabCase(this.props.pathContext.childRegion),
			page,
		});
		navigateTo(link);
	};

	getRegion = region => {
		const childRegion = this.props.data.childRegions.edges.find(
			({ node }) => node.title === region
		);

		return childRegion ? childRegion.node.parent_region : region;
	};

	render() {
		const { parentRegion, childRegion, pageNumber, totalPages } = this.props.pathContext;
		const { page, organisations } = this.props.data;
		const pageSettings = page.edges[0].node;

		const parentRegions = Object.keys(this.regions).map(region => ({
			name: region,
			value: kebabCase(region),
		}));
		const childRegions = parentRegion
			? this.regions[parentRegion].map(region => ({
					name: region,
					value: kebabCase(region),
			  }))
			: null;

		return (
			<React.Fragment>
				<Helmet title="Support Organisations">
					<meta property="og:type" content="website"/>
			    <meta property="og:title" content="InsideOut - Support Organisations"/>
				</Helmet>

				<PageHeader
					background={`#F26C22 url(${BG}) no-repeat 95% 100%`}
					titleText={pageSettings.heading}
					titleTextColor="#fff"
					subTitleText={pageSettings.body}
					subTitleTextColor="#fff"
				/>

				<div className={locationSelectorStyle}>
					<div className={locationSelectorInnerStyle}>
						<label htmlFor="location-state">Browse support organisations in</label>
						<select id="location-state" name="location-state" onChange={this.selectParentRegion}>
							<option value="">Select a State</option>
							{parentRegions.map(region => (
								<option
									key={region.value}
									value={region.value}
									selected={kebabCase(parentRegion) == region.value}
								>
									{region.name}
								</option>
							))}
						</select>

						{childRegions &&
							childRegions.length > 0 && (
								<select
									id="location-region"
									name="location-region"
									onChange={this.selectChildRegion.bind(this)}
								>
									<option value="">Select a Region</option>
									{childRegions.map(region => (
										<option
											key={region.value}
											value={region.value}
											selected={kebabCase(childRegion) == region.value}
										>
											{region.name}
										</option>
									))}
								</select>
							)}
					</div>
				</div>

				<div className={sideBarLayout.mobileFilters}>
					<FilterDropDown
						componentName="Location"
						anchorTo={`.${sideBarLayout.mobileFilters}`}
						className={locationDropdownStyle}
					>
						<div className={sideBarLayout.mobileFiltersDropDown}>
							<label htmlFor="mobile-location-state">Browse support organisations in</label>
							<select
								id="mobile-location-state"
								name="mobile-location-state"
								onChange={this.selectParentRegion}
							>
								<option value="">Select a State</option>
								{parentRegions.map(region => (
									<option
										key={region.value}
										value={region.value}
										selected={kebabCase(parentRegion) == region.value}
									>
										{region.name}
									</option>
								))}
							</select>

							{childRegions && (
								<select
									id="mobile-location-region"
									name="mobile-location-region"
									onChange={this.selectChildRegion.bind(this)}
								>
									<option value="">Select a Region</option>
									{childRegions.map(region => (
										<option
											key={region.value}
											value={region.value}
											selected={kebabCase(childRegion) == region.value}
										>
											{region.name}
										</option>
									))}
								</select>
							)}
						</div>
					</FilterDropDown>
					{/* <FilterDropDown componentName="Type" anchorTo={`.${sideBarLayout.mobileFilters}`}>
						<div className={sideBarLayout.mobileFiltersDropDown}>{this.audienceFilter()}</div>
					</FilterDropDown> */}
				</div>

				<div className={tabPageWrapper}>
					<div className={sideBarLayout.wrapper}>
						<div className={sideBarLayout.leftSide(36)}>
							{organisations &&
								organisations.edges.length > 0 && (
									<Cards columns={1} scrollable={false}>
										{organisations.edges.map(({ node }, index) => (
											<SupportCard
												key={index}
												to={idToLink(node.id)}
												organisation="Organisation"
												location={this.getRegion(node.region)}
												audiences={fullAudiences(node.audiences)}
												type={node.type}
												title={node.title}
												body={node.extract}
												image={
													node.fields && node.fields.thumbnail_image.childImageSharp.resize.src
												}
											/>
										))}
									</Cards>
								)}
						</div>
						{/* <div className={sideBarLayout.rightSide(36)}>
							<aside>
								<h1>Show only for</h1>
								{this.audienceFilter()}
							</aside>
						</div> */}
					</div>
				</div>

				{/* <pre>{JSON.stringify(this.props.pathContext, null, 4)}</pre>
				<hr />
				<pre>{JSON.stringify(this.regions, null, 4)}</pre>
				<hr />
				<pre>{JSON.stringify(organisations, null, 4)}</pre> */}
				{totalPages !== 1 && (
					<Pagination
						initialPage={pageNumber}
						pageCount={totalPages}
						onPageChange={this.onPageChange}
					/>
				)}
			</React.Fragment>
		);
	}
}

export const query = graphql`
	query SupportOrganisationsBrowsePage($regionRegexFilter: String, $skip: Int, $limit: Int) {
		page: allContentJson(
			filter: { id: { regex: "/content\\/support-organisations-page\\.json/" } }
		) {
			edges {
				node {
					heading
					body
				}
			}
		}

		parentRegions: allSupportOrganisationRegionsJson(
			filter: { parent_region: { eq: "" } }
			sort: { fields: [title], order: ASC }
		) {
			edges {
				node {
					title
				}
			}
		}

		childRegions: allSupportOrganisationRegionsJson(
			filter: { parent_region: { ne: "" } }
			sort: { fields: [title], order: ASC }
		) {
			edges {
				node {
					title
					parent_region
				}
			}
		}

		organisations: allSupportOrganisationsJson(
			sort: { order: ASC, fields: [title] }
			filter: { region: { regex: $regionRegexFilter } }
			skip: $skip
			limit: $limit
		) {
			edges {
				node {
					id
					type
					title
					extract
					body
					audiences
					region
					# attachments {
					# 	file
					# }
					contact_information {
						type
						text
						#	destination
					}
					fields {
						thumbnail_image {
							childImageSharp {
								resize(width: 280, height: 280, cropFocus: CENTER) {
									src
								}
							}
						}
					}
				}
			}
		}
	}
`;



// WEBPACK FOOTER //
// ./src/templates/support-organisations/index.js